/**
 * assign-dialog.tsx — Hộp "Giao lại": chọn nhân viên nhận hội thoại (hoặc trả về hàng chờ).
 *
 * Danh sách người nhận do máy chủ lọc (`GET /conversations/:id/assign-candidates`): chỉ người có quyền trên kênh —
 * giao cho người không thấy kênh thì họ cũng không thấy hội thoại. Hiện trạng thái trực tuyến và số hội thoại đang xử lý
 * để người giao chọn người rảnh. Luật quyền ở backend modules/assignment/assign-rules.ts.
 */
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Loader2, Search } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Loading } from '@/components/shared/feedback'
import { useAssignCandidates, useAssignConversation, type AssignCandidate } from '@/hooks/use-conversations'
import { apiError } from '@/lib/api-client'
import { PRESENCE_DOT, PRESENCE_LABEL } from '@/lib/presence'
import { cn, initials } from '@/lib/utils'
import { avatarSrc } from '@/pages/settings/settings-utils'
import { filterCandidates, levelBadge } from './assign-lib'

const NOTE_MAX = 300

export function AssignDialog({
  convId, open, onClose, hasAssignee, convName, onAssigned,
}: { convId: string; open: boolean; onClose: () => void; hasAssignee: boolean; convName?: string; onAssigned?: () => void }) {
  const { t } = useTranslation('convList')
  const { data, isLoading } = useAssignCandidates(convId, open)
  const assign = useAssignConversation(convId)
  const [q, setQ] = useState('')
  const [picked, setPicked] = useState<string | null>(null)
  const [note, setNote] = useState('')

  const list = useMemo(() => filterCandidates(data?.members ?? [], q), [data, q])

  const reset = () => { setQ(''); setPicked(null); setNote('') }
  const close = () => { reset(); onClose() }

  const submit = async (userId: string | null) => {
    try {
      await assign.mutateAsync({ userId, ...(note.trim() ? { note: note.trim() } : {}) })
      toast.success(userId ? t('assignDialog.assigned') : t('assignDialog.returned'))
      close()
      onAssigned?.()
    } catch (e) {
      toast.error(apiError(e))
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base">{hasAssignee ? t('assignDialog.titleReassign') : t('assignDialog.title')}</DialogTitle>
          <DialogDescription className="text-xs">
            {convName ? t('assignDialog.descNamed', { name: convName }) : t('assignDialog.desc')}
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('assignDialog.searchPlaceholder')} className="pl-8" />
        </div>

        <div className="max-h-64 overflow-y-auto rounded-lg border">
          {isLoading ? (
            <Loading label={t('assignDialog.loading')} />
          ) : list.length === 0 ? (
            <p className="p-4 text-center text-sm text-muted-foreground">
              {data?.members.length ? t('assignDialog.noMatch') : t('assignDialog.noMembers')}
            </p>
          ) : (
            <ul className="divide-y">
              {list.map((m: AssignCandidate) => {
                const on = picked === m.id
                const full = m.limit > 0 && m.openCount >= m.limit
                const badge = levelBadge(m.level)
                return (
                  <li key={m.id}>
                    <button
                      type="button"
                      onClick={() => setPicked(m.id)}
                      aria-pressed={on}
                      className={cn('flex w-full items-center gap-2.5 px-3 py-2 text-left', on ? 'bg-primary/10' : 'hover:bg-muted')}
                    >
                      <Avatar className="h-8 w-8">
                        {m.avatarUrl && <AvatarImage src={avatarSrc(m.avatarUrl)} />}
                        <AvatarFallback className="text-[11px]">{initials(m.fullName)}</AvatarFallback>
                      </Avatar>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1.5">
                          <span className="truncate text-sm font-medium">{m.fullName}</span>
                          {badge && <Badge variant="secondary" className="px-1 py-0 text-[10px]">{badge}</Badge>}
                        </span>
                        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <span className={cn('h-2 w-2 rounded-full', PRESENCE_DOT[m.status])} />
                          {PRESENCE_LABEL[m.status]} · {t('assignDialog.openCount', { count: m.openCount })}{full ? t('assignDialog.full') : ''}
                        </span>
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <div className="space-y-1">
          <label htmlFor="assign-note" className="text-xs font-medium">{t('assignDialog.noteLabel')}</label>
          <textarea
            id="assign-note"
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
            rows={2}
            placeholder={t('assignDialog.notePlaceholder')}
            className="flex w-full rounded-md border border-input bg-card px-3 py-1.5 text-sm placeholder:text-muted-foreground focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/15"
          />
          <p className="text-right text-[11px] text-muted-foreground">{note.length}/{NOTE_MAX}</p>
        </div>

        <DialogFooter className="flex-wrap gap-2 sm:justify-between">
          {hasAssignee ? (
            <Button variant="ghost" size="sm" disabled={assign.isPending} onClick={() => submit(null)} title={t('assignDialog.returnHint')}>
              {t('assignDialog.returnBtn')}
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={close} disabled={assign.isPending}>{t('common:actions.cancel')}</Button>
            <Button onClick={() => picked && submit(picked)} disabled={!picked || assign.isPending}>
              {assign.isPending && <Loader2 className="animate-spin" />} {t('assign.assign')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
